import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocation, useNavigate } from "react-router-dom";

import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { LoadSpinner } from "features/LoadSpinner";

import { PageHeader } from "components/PageHeader";

import { MeetingMConfService } from "services/meetingMConf";
import { HearingService } from "services/hearing";
import api from "services/api";

import { usePlaceGate } from "hooks/usePlaceGate";

import { useQuery } from "utils/get";

import * as Styled from "./styles";

export const FullRecordingView: React.FC = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { user } = useAppSelector((state) => state.user);
  const { userCustomerProfile } = useAppSelector((state) => state.profile);

  const [loading, setLoading] = useState(true);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);

  const hearingFileId = query.get("hearingFileId");
  const placeId = query.get("placeId");
  const audienceNumber = query.get("audienceNumber");
  const audience = location.state?.audience;

  const guard = usePlaceGate({ placeId: placeId });

  const authorized = guard.isAllowed;
  const checkingAuthorization = !guard.isReady;

  const isAdministrator = useMemo(() => {
    const profileName =
      userCustomerProfile?.profileName?.toString()?.toLowerCase?.() || "";
    const profileType = userCustomerProfile?.profileType?.toString() || "";
    return (
      profileType === "FORMS_ADMIN" ||
      profileName.includes("admin") ||
      profileName.includes("administrador")
    );
  }, [userCustomerProfile]);

  const userDistrictIds = useMemo<number[]>(() => {
    const districtsArr =
      (userCustomerProfile as any)?.districts || (user as any)?.districts || [];
    const base = Array.isArray(districtsArr)
      ? districtsArr.map((d: any) => Number(d?.id ?? d))
      : [];

    const extras = [
      Number((userCustomerProfile as any)?.codeDistrict),
      Number((userCustomerProfile as any)?.districtId),
      Number((user as any)?.codeDistrict),
      Number((user as any)?.districtId),
      ...((userCustomerProfile as any)?.places || (user as any)?.places || [])
        .map((p: any) => Number(p?.districtId))
        .filter((n: any) => Number.isFinite(n)),
      Number((userCustomerProfile as any)?.place?.districtId),
      Number((user as any)?.place?.districtId),
    ].filter((n) => Number.isFinite(n)) as number[];

    return Array.from(new Set([...base, ...extras]));
  }, [userCustomerProfile, user]);

  const userUnitIds = useMemo<number[]>(() => {
    const units =
      (userCustomerProfile as any)?.units || (user as any)?.units || [];
    const places =
      (userCustomerProfile as any)?.places || (user as any)?.places || [];
    const allowedUnits =
      (userCustomerProfile as any)?.allowedUnits ||
      (user as any)?.allowedUnits ||
      [];

    const base = [
      ...(Array.isArray(units)
        ? units.map((u: any) => Number(u?.id ?? u))
        : []),
      ...(Array.isArray(places)
        ? places.map((p: any) => Number(p?.id ?? p))
        : []),
      ...(Array.isArray(allowedUnits)
        ? allowedUnits.map((u: any) => Number(u?.id ?? u))
        : []),
    ];

    const extras = [
      Number((userCustomerProfile as any)?.placeId),
      Number((user as any)?.placeId),
      Number((userCustomerProfile as any)?.place?.id),
      Number((user as any)?.place?.id),
    ].filter((n) => Number.isFinite(n));

    return Array.from(new Set([...base, ...extras])).filter((n) =>
      Number.isFinite(n),
    ) as number[];
  }, [userCustomerProfile, user]);

  useEffect(() => {
    let isActive = true;

    async function getHearingIdFromFileId(fileId: string | number) {
      try {
        const { data } = await api.get(
          `/v1/HearingIndex/GetAllByHearingFileId?hearingFileId=${fileId}`,
        );
        const first = data?.data?.[0];
        return Number(first?.hearingId) || null;
      } catch {
        return null;
      }
    }

    async function getHearingPayloadByFileId(fileId: string | number) {
      const hearingId = await getHearingIdFromFileId(fileId);
      if (!hearingId) return null;
      try {
        const response = await HearingService.getById(hearingId);
        const payload = response?.data?.data ?? response?.data ?? response;
        return payload || null;
      } catch {
        return null;
      }
    }

    async function enforceAuthorizationAndFetch() {
      if (!hearingFileId || !user?.customerId) {
        addToast({
          type: "error",
          title: "Erro",
          description: "Parâmetros necessários não encontrados.",
        });
        if (isActive) setLoading(false);
        return;
      }

      if (!guard.isReady) {
        return;
      }

      try {
        const hearingPayload = await getHearingPayloadByFileId(hearingFileId);

        const hearingDistrictId = Number(
          hearingPayload?.districtId ??
            hearingPayload?.comarcaId ??
            hearingPayload?.countyId ??
            hearingPayload?.district?.id ??
            hearingPayload?.place?.districtId,
        );

        const hearingUnitId = Number(
          hearingPayload?.unitId ??
            hearingPayload?.varaId ??
            hearingPayload?.courtUnitId ??
            hearingPayload?.unit?.id ??
            hearingPayload?.placeId ??
            hearingPayload?.place?.id,
        );

        const hasSameDistrict = hearingDistrictId
          ? userDistrictIds.includes(hearingDistrictId)
          : false;

        const hasSameUnit = hearingUnitId
          ? userUnitIds.includes(hearingUnitId)
          : false;

        const isAllowed = Boolean(
          authorized || isAdministrator || hasSameUnit || hasSameDistrict,
        );

        if (!isAllowed) {
          addToast({
            type: "error",
            title: "Acesso bloqueado",
            description: t("audiences.messages.notBelongsToUnit"),
          });
          navigate("/dashboard", { replace: true });
          return;
        }
      } catch {
        addToast({
          type: "error",
          title: "Erro",
          description: t("audiences.messages.accessValidationError"),
        });
        navigate("/dashboard", { replace: true });
        return;
      }

      try {
        const response = await MeetingMConfService.getLinkRecording(
          user.customerId,
          hearingFileId,
        );
        if (response?.status && response?.data) {
          if (isActive) setRecordingUrl(response.data);
        } else {
          addToast({
            type: "error",
            title: "Visualização total",
            description:
              response?.message ??
              "Não foi possível obter o link de visualização da gravação.",
          });
        }
      } catch {
        addToast({
          type: "error",
          title: "Erro",
          description: "Ocorreu um erro ao tentar obter a gravação.",
        });
      } finally {
        if (isActive) setLoading(false);
      }
    }

    if (checkingAuthorization) {
      return;
    }

    enforceAuthorizationAndFetch();
    return () => {
      isActive = false;
    };
  }, [
    hearingFileId,
    user?.customerId,
    addToast,
    userDistrictIds,
    userUnitIds,
    navigate,
    isAdministrator,
    authorized,
    checkingAuthorization,
    guard.isReady,
  ]);

  return (
    <Styled.Container>
      <PageHeader
        label={audienceNumber ? t("audiences.fullView", { number: audienceNumber }) : t("audiences.fullViewDefault")}
        background="primary"
        backClick={() => navigate(`/audiences`, { state: audience })}
        height="80px"
      />

      <Styled.Content>
        {checkingAuthorization || loading ? (
          <LoadSpinner loading />
        ) : recordingUrl ? (
          <Styled.IframeContainer>
            <iframe title="visualizacao-total" src={recordingUrl} allowFullScreen />
          </Styled.IframeContainer>
        ) : (
          <Styled.ErrorContainer>
            <p>Não foi possível carregar a gravação.</p>
          </Styled.ErrorContainer>
        )}
      </Styled.Content>
    </Styled.Container>
  );
};

export default FullRecordingView;
